/** @jsxImportSource @emotion/react */
import _ from 'lodash';
import React, { useEffect, useState, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Form, Field } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { FieldArray } from 'react-final-form-arrays';
import DatePicker from 'react-datepicker';
import {enUS, frCA} from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import { connect } from 'react-redux';
import { Spinner } from 'reactstrap';
import { ThunkDispatch } from '../../actions/utils';
import { fetchProposal } from '../../actions/proposals';
import { getLocale } from 'react-i18nify';
import { StateShape as ReduxStateShape } from '../../reducers';
import t from '../../localization/i18n';
import * as repr from '../../api/types';
import Button from '../Button';
import { FieldTextRequired, WhenFieldChanges } from '../shared/CustomFormFields';
import ModalBase from '../shared/ModalBase';
import clientFormStyles from './ClientDetailsForm.styles';
import { registerLocale } from 'react-datepicker';

registerLocale('en-US', enUS);
registerLocale('fr-CA', frCA);

const CustomDateInput = ({ value, onClick }: { value: string; onClick: () => void }) => {
    return (
        <input
            id="dob"
            type="text"
            value={value.split('T')[0]}
            onClick={onClick}
            readOnly
        />
    );
};

interface PropsShape {
    isVisible: boolean;
    toggleModal: () => void;
    saveProposal: (proposal: Partial<repr.ProposalDetailed>) => Promise<void>;
    investmentType?: repr.investmentTypes; // needed for the payload to create a new client
    proposalId?: string;

    // from store
    dispatch: ThunkDispatch;
    proposal: repr.ProposalDetailed | null;
}

export interface ClientData {
    proposalType: 'Individual' | 'Household';
    advisorFirstName: string;
    advisorLastName: string;
    proposalName: string;
    clients: repr.ClientDetailed[];
}

const MAX_NUMBER_OF_MEMBERS = 10;

// these are keys used to access translations
const salutations = ['', 'mr', 'mrs', 'miss', 'ms', 'dr'];

const clientDefaultValues = {
    salutation: '',
    firstName: '',
    lastName: '',
    amount: 0,
    latestReportId: '',
    accounts: [],
    dob: null,
};

const getFirstErrorInput = (errors: any): HTMLElement | null => {
    if (!errors) {
        return null;
    }

    const formElement = document.getElementById('add-client-form') as HTMLFormElement | null;
    if (!formElement) {
        return null;
    }

    const inputs = Array.from(formElement.elements) as Array<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | HTMLButtonElement
    >;

    return (
        inputs.find((input) => {
            if (!input.name || input.disabled) {
                return false;
            }
            if (input instanceof HTMLInputElement && input.type === 'hidden') {
                return false;
            }

            return _.get(errors, input.name) !== undefined;
        }) || null
    );
};

const focusOnError = (form: any) => {
    const originalSubmit = form.submit;

    form.submit = () => {
        const result = originalSubmit.call(form);

        const { errors, submitErrors } = form.getState();
        const allErrors =
            errors && Object.keys(errors).length > 0
                ? errors
                : submitErrors && Object.keys(submitErrors).length > 0
                ? submitErrors
                : null;

        const targetInput = getFirstErrorInput(allErrors);
        if (targetInput && typeof targetInput.focus === 'function') {
            targetInput.focus();
        }

        return result;
    };

    return () => {
        form.submit = originalSubmit;
    };
};

const ClientDetailsForm = (props: PropsShape) => {
    const { proposalId, proposal, dispatch } = props;
    const datePickerRef = useRef<DatePicker | null>(null);

    // only show loading spinner if a proposal needs to be fetched
    const [isLoading, setIsLoading] = useState(!!proposalId);
    const [isBirthdayConfirmationCheck, setIsBirthdayConfirmationCheck] = useState(false);
    const [isBirthdayTextVisible, setIsBirthdayTextVisible] = useState(false);

    const errorMessages: string[] = [];

    if (proposalId && !proposal) {
        errorMessages.push(t('clientList.clientDetails.fetchDetailsFailed'));
    }

    useEffect(() => {
        if (proposalId) {
            const locale = getLocale();
            dispatch(fetchProposal(proposalId, locale))
                .then(() => setIsLoading(false))
                .catch((e) => {
                    setIsLoading(false);
                    return console.error(`Failed to fetch proposal ${proposalId}, error: ${e}`);
                });
        }
    }, [proposalId, dispatch]);

    let initialValues: ClientData | repr.ProposalDetailed = {
        proposalType: 'Individual',
        advisorFirstName: '',
        advisorLastName: '',
        proposalName: '',
        clients: [{ clientId: uuidv4(), ...clientDefaultValues }],
    };

    // avoid showing data from a different proposal before current one is loaded
    // fields will show default initial values for new client until loaded
    if (proposalId && proposal && proposal.id === proposalId) {
        initialValues = _.cloneDeep(proposal);
    }

    const convertUTCToLocalDate = (date: Date) => {
        if (!date) {
            return date;
        }
        date = new Date(date);
        date = new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
        return date;
    };

    const handleSubmit = (values: ClientData | repr.ProposalDetailed) => {
        let payload = values;

        if (!proposalId) {
            // if a new proposal/client is being created
            payload = {
                proposalName: values.proposalName,
                advisorFirstName: values.advisorFirstName,
                advisorLastName: values.advisorLastName,
                amount: 0,
                investmentType: props.investmentType,
                proposalType: values.proposalType,
                includeGIA: false,
                isFeeBased: false,
                includeServiceFee: false,
                includeFundProfiles: false,
                overrideMinimums: false,
                clients: values.clients,
            };
        }

        props
            .saveProposal(payload)
            // close modal if validation is successful AND the api call is successful
            .then(() => props.toggleModal())
            .catch(() =>
                props.proposalId
                    ? console.log('editing client faild')
                    : console.log('creating new client failed')
            );
    };

    const memberList = (
        values: ClientData | repr.ProposalDetailed,
        push: (key: string, value: repr.ClientDetailed) => void
    ) => (
        <>
            <div className="members">
                <FieldArray name="clients">
                    {({ fields }) =>
                        fields.map((m, index) => {
                            const member = fields.value[index];
                            const memberFullName = `${member.firstName} ${member.lastName}`;

                            return (
                                <div key={index}>
                                    <div className="input-row" key={`${m}`}>
                                        {/* Salutations dropdown */}
                                        {/* The field below needs the parse function because the
                                    saved value can be an empty string and final form removes
                                    empty values by default, setting the field to null */}
                                        {/* https://github.com/final-form/react-final-form/issues/130 */}
                                        <div className="label-input-pair">
                                            <label htmlFor={`${m}.salutation`}>
                                                {t('clientList.clientDetails.salutation')}
                                            </label>
                                            <Field
                                                name={`${m}.salutation`}
                                                component="select"
                                                parse={(value) => value}
                                            >
                                                {salutations.map((s) => (
                                                    <option key={`${m}${s}`} value={s}>
                                                        {t(`salutations.${s}`) || '-'}
                                                    </option>
                                                ))}
                                            </Field>
                                        </div>

                                        <FieldTextRequired
                                            name={`${m}.firstName`}
                                            labelKey="application.firstName"
                                        />

                                        <FieldTextRequired
                                            name={`${m}.lastName`}
                                            labelKey="application.lastName"
                                        />

                                        {/* if there is onlu one member, not allowed to delete */}
                                        {fields.length !== 1 && (
                                            <Button
                                                styles={{ marginTop: '4%', paddingLeft: '0' }}
                                                variant="icon"
                                                type="button"
                                                onClick={() => fields.remove(index)}
                                                tooltip={{
                                                    id: `toolTip--${proposalId}-${index}`,
                                                    text: t('application.delete'),
                                                }}
                                            >
                                                <span
                                                    className="icon icon--delete"
                                                    aria-hidden="true"
                                                />
                                                <span className="sr-only">
                                                    {t({
                                                        key: 'clientList.alt.deleteMember',
                                                        values: {
                                                            name: memberFullName || '',
                                                        },
                                                    })}
                                                </span>
                                            </Button>
                                        )}
                                    </div>
                                    <div className="input-row">
                                        {values.proposalType === 'Individual' &&
                                            props.investmentType &&
                                            props.investmentType.toLowerCase() === 'seg' && (
                                                <div className="input-section">
                                                    <div className="label-input-pair">
                                                        <label htmlFor={`${m}.dob`}>
                                                            {t('application.birthday')}
                                                        </label>
                                                        <Field
                                                            name={`${m}.dob`}
                                                            render={({ input }) => {
                                                                const formattedValue = input.value
                                                                    ? convertUTCToLocalDate(
                                                                          new Date(input.value)
                                                                      )
                                                                    : null;
                                                                return (
                                                                    <DatePicker
                                                                        {...input}
                                                                        selected={formattedValue}
                                                                        onChange={(
                                                                            date: Date | null
                                                                        ) => {
                                                                            input.onChange(
                                                                                date
                                                                                    ? new Date(date)
                                                                                    : null
                                                                            );
                                                                        }}
                                                                        locale={getLocale()}
                                                                        showYearDropdown
                                                                        scrollableYearDropdown
                                                                        yearDropdownItemNumber={130}
                                                                        ref={datePickerRef}
                                                                        dateFormat={'yyyy-MM-dd'}
                                                                        maxDate={new Date()}
                                                                        customInput={
                                                                            <CustomDateInput
                                                                                value={input.value}
                                                                                onClick={() => {
                                                                                    if (
                                                                                        datePickerRef.current
                                                                                    ) {
                                                                                        datePickerRef.current.setOpen(
                                                                                            true
                                                                                        );
                                                                                    }
                                                                                }}
                                                                            />
                                                                        }
                                                                    />
                                                                );
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                    </div>
                                </div>
                            );
                        })
                    }
                </FieldArray>
            </div>

            {/* Button to add household members */}
            {values.proposalType === 'Household' && (
                <>
                    <Button
                        type="button"
                        variant="secondary"
                        disabled={values.clients.length >= MAX_NUMBER_OF_MEMBERS}
                        onClick={() =>
                            push('clients', {
                                clientId: uuidv4(),
                                ...clientDefaultValues,
                            })
                        }
                    >
                        <span
                            className="btn-icon icon icon--plus-circle-white"
                            aria-hidden="true"
                        />
                        <span className="sr-only">{t('clientList.alt.addMember')}</span>
                        {t('clientList.clientDetails.addMember')}
                    </Button>

                    {values.clients.length >= MAX_NUMBER_OF_MEMBERS && (
                        <span> {t('clientList.clientDetails.maxMembers')}</span>
                    )}
                </>
            )}
        </>
    );

    return (
        <Form
            onSubmit={handleSubmit}
            initialValues={initialValues}
            mutators={{ ...arrayMutators }}
            decorators={[focusOnError]}
            render={({
                handleSubmit,
                form: {
                    mutators: { push },
                },
                // injected from final-form-arrays above
                pristine,
                form,
                submitting,
                invalid,
                values,
            }) => {
                // Early return if values is undefined
                if (!values) return null;
                
                // Accessing the state for clients[0].dob
                const dirtyFields = form.getState().dirtyFields || {};
                const isDobDirty = dirtyFields['clients[0].dob'];
                const initialValues = form.getState().initialValues as ClientData | undefined;
                let initialDOBvalue = initialValues?.clients?.[0]?.dob || null;
                const initialDOBvalueModified =
                    initialDOBvalue !== null
                        ? new Date(initialDOBvalue).toISOString().split('T')[0]
                        : null;
                let headerText = t('clientList.clientDetails.editHeading');

                if (!proposalId && props.investmentType) {
                    // if the proposal ID does not exist, a client is being created
                    headerText = `${t('clientList.clientDetails.createHeading')} ${t(
                        `clientList.${props.investmentType.toLowerCase()}`
                    )}`;
                } else if (!proposalId) {
                    // in case the investment type is not passed
                    headerText = t('clientList.clientDetails.createHeading');
                }

                const BirthdayHeader = (
                    <div id="birthday-header-text" className="hide">
                        {t('clientList.clientDetails.birthdayHeader')}
                    </div>
                );

                const BirthdayConfirmationChange = (
                    <div id="birthday-confirmation-text" className="hide">
                        {t('clientList.clientDetails.birthdayChangeConfirmation')}
                    </div>
                );

                const header = (
                    <React.Fragment>
                        <div id="header-text">{headerText}</div>
                        {BirthdayHeader}
                        {BirthdayConfirmationChange}
                    </React.Fragment>
                );

                const formFields = (
                    <form id="add-client-form" onSubmit={handleSubmit}>
                        {/* Client Type */}
                        <div className="client-type-toggle input-section">
                            <label>
                                <Field
                                    name="proposalType"
                                    component="input"
                                    type="radio"
                                    value="Individual"
                                />
                                {t('clientList.individual')}
                            </label>
                            <label>
                                <Field
                                    name="proposalType"
                                    component="input"
                                    type="radio"
                                    value="Household"
                                />
                                {t('clientList.household')}
                            </label>

                            <WhenFieldChanges
                                field="proposalType"
                                becomes="Individual"
                                set="clients"
                                // keep the first client's details when switching to individual
                                to={[values.clients[0]]}
                            />
                        </div>

                        {/* Advisor Information */}
                        <div className="input-section">
                            <h5 className="subtitle">
                                {t('clientList.clientDetails.advisorInformation')}
                            </h5>

                            <div className="input-row">
                                <FieldTextRequired
                                    name="advisorFirstName"
                                    labelKey="application.firstName"
                                />
                                <FieldTextRequired
                                    name="advisorLastName"
                                    labelKey="application.lastName"
                                />
                            </div>
                        </div>

                        {/* Client List */}
                        <div className="input-section">
                            <h5 className="subtitle">
                                {t('clientList.clientDetails.clientInformation')}
                            </h5>

                            {memberList(values, push)}
                        </div>

                        {/* Household Name */}
                        {values.proposalType === 'Household' && (
                            <div className="input-section">
                                <FieldTextRequired
                                    name="proposalName"
                                    labelKey="clientList.clientDetails.householdName"
                                />
                            </div>
                        )}

                        {/* -- uncomment for easier debugging of form values -- */}
                        {/* <pre>{JSON.stringify(values, null, 2)}</pre> */}
                    </form>
                );

                const formElement = document.getElementById('add-client-form');
                const headerElement = document.getElementById('header-text');
                const birthdayHeaderElement = document.getElementById('birthday-header-text');
                const buttonDefaultText = document.getElementById('button-default-text');
                const birthdayDefaultText = document.getElementById('birthday-default-text');
                const birthdayConfirmationText = document.getElementById(
                    'birthday-confirmation-text'
                );
                const datePickerInput = document.getElementById('dob');

                // Toggles visibility of birthday text and handles form submission
                const onSubmit = () => {
                    if (isBirthdayTextVisible) {
                        if (proposalId) {
                            // resetting holdings if a proposal exists due to date change
                            values.clients.forEach((c, clientIndex) => {
                                c.accounts.forEach((a, accountIndex) => {
                                    (form.change as any)(
                                        `clients[${clientIndex}].accounts[${accountIndex}].holdings`,
                                        []
                                    );
                                });
                            });

                            // Use setTimeout to ensure DOM updates are done before manipulating classes
                            setTimeout(() => {
                                if (
                                    formElement &&
                                    headerElement &&
                                    birthdayHeaderElement &&
                                    buttonDefaultText &&
                                    birthdayDefaultText &&
                                    birthdayConfirmationText
                                ) {
                                    formElement.classList.remove('hide');
                                    headerElement.classList.remove('hide');
                                    buttonDefaultText.classList.remove('hide');
                                    birthdayHeaderElement.classList.add('hide');
                                    birthdayDefaultText.classList.add('hide');
                                    birthdayConfirmationText.classList.add('hide');
                                    setIsBirthdayConfirmationCheck(true);
                                    setIsBirthdayTextVisible(false);
                                }
                            }, 0); // Immediate next cycle
                        } else {
                            form.submit();
                        }
                    } else {
                        if (proposalId) {
                            form.submit();
                        }

                        setIsBirthdayTextVisible(true);

                        if (
                            formElement &&
                            headerElement &&
                            birthdayHeaderElement &&
                            buttonDefaultText &&
                            birthdayDefaultText &&
                            values.clients[0].dob === null
                        ) {
                            formElement.classList.add('hide');
                            headerElement.classList.add('hide');
                            buttonDefaultText.classList.add('hide');
                            birthdayHeaderElement.classList.remove('hide');
                            birthdayDefaultText.classList.remove('hide');
                        } else {
                            form.submit();
                        }
                    }
                };

                // Toggles visibility of birthday text and form field
                const onCancel = () => {
                    if (isBirthdayTextVisible) {
                        setIsBirthdayTextVisible(false);
                        if (
                            formElement &&
                            headerElement &&
                            birthdayHeaderElement &&
                            buttonDefaultText &&
                            birthdayDefaultText &&
                            birthdayConfirmationText
                        ) {
                            formElement.classList.remove('hide');
                            headerElement.classList.remove('hide');
                            buttonDefaultText.classList.remove('hide');
                            birthdayHeaderElement.classList.add('hide');
                            birthdayDefaultText.classList.add('hide');
                            birthdayConfirmationText.classList.add('hide');

                            if (proposalId) {
                                // resets the dob field if proposal exist
                                (form.change as any)('clients[0].dob', initialDOBvalueModified);
                            }
                        }
                    } else {
                        // closes the modal
                        props.toggleModal();
                    }
                };

                if (datePickerInput) {
                    // Checks if dob is modified when editing
                    if (
                        isDobDirty &&
                        proposalId &&
                        values.clients[0].dob !== initialDOBvalueModified
                    ) {
                        if (!isBirthdayConfirmationCheck) {
                            if (
                                formElement &&
                                headerElement &&
                                birthdayConfirmationText &&
                                buttonDefaultText &&
                                birthdayDefaultText
                            ) {
                                setIsBirthdayTextVisible(true);
                                formElement.classList.add('hide');
                                headerElement.classList.add('hide');
                                buttonDefaultText.classList.add('hide');
                                birthdayConfirmationText.classList.remove('hide');
                                birthdayDefaultText.classList.remove('hide');
                            }
                        }
                    }
                }

                let submitButtons = (
                    <>
                        <Button onClick={() => onCancel()}>{t('application.cancel')}</Button>
                        <Button
                            type="submit"
                            variant="primary"
                            disabled={submitting || pristine}
                            onClick={() => form.submit()}
                        >
                            {proposalId
                                ? t('clientList.clientDetails.save')
                                : t('clientList.clientDetails.create')}
                        </Button>
                    </>
                );

                if (
                    values.proposalType === 'Individual' &&
                    props.investmentType &&
                    props.investmentType.toLowerCase() === 'seg'
                ) {
                    submitButtons = (
                        <>
                            <Button onClick={() => onCancel()}>{t('application.cancel')}</Button>
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={submitting || pristine || invalid}
                                onClick={() => onSubmit()}
                            >
                                <span id="button-default-text">
                                    {proposalId
                                        ? t('clientList.clientDetails.save')
                                        : t('clientList.clientDetails.create')}
                                </span>
                                <span id="birthday-default-text" className="hide">
                                    {t('application.yes')}
                                </span>
                            </Button>
                        </>
                    );
                }

                let modalBody = null;

                if (isLoading) {
                    modalBody = (
                        <div className="loading-spinner">
                            <Spinner color="dark" />
                            {'  Loading'}
                        </div>
                    );
                } else if (errorMessages.length) {
                    modalBody = errorMessages.map((e) => <span key={e}>{e}</span>);
                }

                return (
                    <>
                        <ModalBase
                            styles={clientFormStyles}
                            header={header}
                            body={modalBody || formFields}
                            footer={submitButtons}
                            isVisible={props.isVisible}
                            toggleModal={props.toggleModal}
                        />
                    </>
                );
            }}
        />
    );
};

const mapStateToProps = (state: ReduxStateShape) => {
    const proposal = state.app.proposal;

    return { proposal };
};

export default connect(mapStateToProps)(ClientDetailsForm);
